// Shared functionality between practice exams and full exams
// Nest inside the main component, which should handle `exerciseIds` and `progressIndex`
export const lazyLoadExercises = (exercisesUrl) => ({
  showVehicleInfo: false,
  exerciseBatchSize: 20,
  exercisesLoaded: false,

  async init() {
    await this.fetchExercises(0, this.exerciseBatchSize)

    this.$watch('currentIndex', async () => {
      // Hide vehicle info every time when switching to a different exercise
      this.showVehicleInfo = false

      // Get new exercises if not loaded yet
      const nextIdx = this.currentIndex + 1
      const exercisesRemaining = this.progressIndex < this.exerciseIds.length - 1
      if (exercisesRemaining && nextIdx % this.exerciseBatchSize === 0) {
        await this.fetchExercises(nextIdx, nextIdx + this.exerciseBatchSize)
      }
    })
  },

  async fetchExercises(fromIdx, toIdx) {
    const body = {
      exercise_ids: this.exerciseIds.slice(fromIdx, toIdx),
      start_idx: fromIdx,
    }
    const request = new FetchRequest('post', exercisesUrl, { body })
    const response = await request.perform()

    if (response.ok) {
      const html = await response.html
      this.$root.querySelector('footer').insertAdjacentHTML('beforebegin', html)
    }

    // This is used to hide the footer before exercises get loaded so it doesn't cause any layout shifts
    this.exercisesLoaded = true
  },

  // Render the exercise content beforehand to load images in the background (slides are hidden with x-show)
  // Load in batches of 5 instead of 1 at a time to take advantage of parallel requests
  renderSlide(index) {
    return Math.floor((index - 1) / 5) <= Math.floor(this.progressIndex / 5) || index === 0
  },
})

// Exam container for practice exams
export const practiceExam = (I18n, exercises, trafficSignExercises = null) => ({
  exercises,
  trafficSignExercises,
  exerciseIds: trafficSignExercises?.map((signs) => signs.map(({ id }) => id)) || exercises.map(({ id }) => id),
  currentIndex: 0,

  async init() {
    this.exercises.forEach((exercise) => {
      exercise.answered = false
      exercise.correct = false
    })
  },
  result() {
    return `${this.correctCount}/${this.exercises.length}`
  },
  resultText([res1, res2, res3, res4]) {
    const correctPercentage = (this.correctCount * 100) / this.exercises.length
    if (correctPercentage < 60) return res1
    if (correctPercentage < 80) return res2
    if (correctPercentage < 90) return res3
    return res4
  },

  get correctCount() {
    return this.exercises.reduce((memo, exercise) => (exercise.correct ? memo + 1 : memo), 0)
  },
  get exercise() {
    return this.exercises[this.currentIndex]
  },
  get progressIndex() {
    return this.currentIndex
  },

  AnswerResult: {
    [':class']() {
      const animationClass = this.exercise.correct ? 'correct-color-anim' : 'incorrect-color-anim'
      return `answer-result ${animationClass}`
    },
    ['x-text']() {
      return this.exercise.correct ? I18n.correct_answer : I18n.incorrect_answer
    },
    ['x-show']() {
      return this.exercise.answered
    },
    'x-cloak': '',
  },
})

// Single exercise
export const practiceExercise = (correctAnswer) => ({
  correctAnswer,

  answer(selectedAnswer) {
    this.exercise.correct = selectedAnswer === this.correctAnswer
    this.exercise.answered = true

    // Scroll to the bottom of the controls if correct or to the top of the answers wrapper if incorrect
    const scrollTarget = this.exercise.correct
      ? [document.body, 'end']
      : [this.$el.closest('.practice-exercise-options')]
    this.scrollToTarget(...scrollTarget)
  },
  scrollToTarget(element, verticalAlignment = 'start') {
    setTimeout(() => {
      // NOTE: When using this function, the element needs to have some scroll-margin ($scroll-margin-top)
      //       in the CSS to make sure it doesn't disappear behind the page header.
      element.scrollIntoView({ behavior: 'smooth', block: verticalAlignment })
    }, 100)
  },
})

// Single answer button
export const answerElement = (isCorrect, value) => ({
  isCorrect,
  value,
  selected: false,

  answerButton: {
    [':class']() {
      return (
        this.exercise.answered && {
          'practice-exercise-correct': this.isCorrect,
          'practice-exercise-incorrect': this.selected && !this.isCorrect,
          'show-right-option': !this.selected && this.isCorrect,
          'option-selected': this.selected,
        }
      )
    },
    ['@click']() {
      // Prevent answering a second time
      if (this.exercise.answered) {
        return
      }

      this.selected = true
      this.answer(this.value)
    },
  },
  signName: {
    [':class']() {
      return (
        this.exercise.answered && {
          'incorrect-color-anim': this.selected && !this.isCorrect,
          'correct-color-anim': this.isCorrect,
        }
      )
    },
  },
})

// Handle displaying traffic sign variants
export const variants = () => ({
  showVariants: false,

  toggle() {
    this.showVariants = !this.showVariants
    if (this.showVariants) this.scrollToTarget(this.$el)
  },
})
